import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  CheckCircle2,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  Pencil,
  Play,
  Power,
  RefreshCw,
  RotateCcw,
  Save,
  ShieldCheck,
  Trash2,
  Upload,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import { api, ApiError } from '../api/client.js';
import { CredBlock } from '../components/CredBlock.jsx';
import { Button } from '../components/Button.jsx';
import { Card, CardHeader } from '../components/Card.jsx';
import { Banner } from '../components/Banner.jsx';
import { Table, THead, TBody, TR, TH, TD, Empty } from '../components/Table.jsx';
import { Input, Textarea, Select, FormError } from '../components/Field.jsx';
import { Checkbox } from '../components/Checkbox.jsx';
import { Pill } from '../components/Pill.jsx';
import { DnsPreview, DnsOutcome, useDnsPreview } from '../components/DnsPreview.jsx';

export function SiteDetail() {
  const { domain } = useParams();
  const [state, setState] = useState({ status: 'loading' });

  const load = () => {
    setState({ status: 'loading' });
    api(`/api/sites/${encodeURIComponent(domain)}`)
      .then((data) => setState({ status: 'ok', data }))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          setState({ status: 'not_found' });
        } else {
          setState({ status: 'error', message: err.message });
        }
      });
  };

  useEffect(load, [domain]);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/sites"
            className="inline-flex items-center gap-1.5 text-[13px] text-t3 hover:text-blue"
          >
            <ArrowLeft className="w-4 h-4" />
            Sites
          </Link>
        </div>
        {state.status === 'ok' && (
          <Button size="sm" icon={RefreshCw} onClick={load} title="Reload">
            Refresh
          </Button>
        )}
      </div>

      {state.status === 'loading' && <LoadingCard />}
      {state.status === 'not_found' && <NotFoundCard domain={domain} />}
      {state.status === 'error' && <ErrorCard message={state.message} />}
      {state.status === 'ok' && <DetailSections data={state.data} onRefetch={load} />}
    </div>
  );
}

// ─── Status cards ──────────────────────────────────────────────────────────

function LoadingCard() {
  return (
    <Card className="flex items-center justify-center gap-2 p-12 text-[13.5px] text-t3">
      <Loader2 className="w-4 h-4 animate-spin" />
      Loading site…
    </Card>
  );
}

function NotFoundCard({ domain }) {
  return (
    <Banner
      kind="note"
      title="Site not found"
      action={<Button as={Link} to="/sites">Back to sites</Button>}
    >
      <span className="font-mono">{domain}</span> isn&apos;t in the WordPress
      agent&apos;s registry. Nothing was changed — this is a read-only lookup.
    </Banner>
  );
}

function ErrorCard({ message }) {
  return (
    <Banner kind="error" title="Couldn't load this site">
      {message} — nothing has changed. The site itself and its backups are
      unaffected; only this view failed to read.
    </Banner>
  );
}

// ─── Main sections ─────────────────────────────────────────────────────────

function DetailSections({ data, onRefetch }) {
  const { site, status, maintenance } = data;
  return (
    <div className="space-y-5 max-w-4xl">
      <OverviewCard site={site} status={status} maintenance={maintenance} />
      <MetadataCard site={site} onRefetch={onRefetch} />
      <CredentialsCard domain={site.domain} />
      <BackupsCard domain={site.domain} />
      <ActionsCard
        domain={site.domain}
        maintenance={maintenance}
        wpRunning={!!status?.wp?.running}
        onRefetch={onRefetch}
      />
    </div>
  );
}

// ─── Overview (vps1 truth + container state) ───────────────────────────────

function OverviewCard({ site, status, maintenance }) {
  const siteUrl = `https://${site.domain}`;
  return (
    <SectionCard title="Overview" icon={CheckCircle2}>
      <Row label="Domain">
        <a
          href={siteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-mono text-[13px] text-t1 hover:text-blue hover:underline"
        >
          {site.domain}
          <ExternalLink className="w-3 h-3 text-ic-light" />
        </a>
      </Row>
      <Row label="Site name">{site.site_name || <Muted />}</Row>
      <Row label="Client">{site.client_name || <Muted />}</Row>
      <Row label="Admin email">
        {site.admin_email ? (
          <span className="font-mono text-[12.5px] text-t2">{site.admin_email}</span>
        ) : (
          <Muted />
        )}
      </Row>
      <Row label="Site dir">
        {site.site_dir ? (
          <span className="break-all font-mono text-[12.5px] text-t3">{site.site_dir}</span>
        ) : (
          <Muted />
        )}
      </Row>
      <Row label="Created">
        <DateCell value={site.created_at} />
      </Row>
      <Row label="Containers">
        <div className="flex flex-wrap gap-1.5 items-center">
          <ContainerBadge role="WP" info={status?.wp} />
          <ContainerBadge role="DB" info={status?.db} />
          <MaintenanceBadge maintenance={maintenance} wpRunning={!!status?.wp?.running} />
        </div>
      </Row>
    </SectionCard>
  );
}

/**
 * Bordered icon+label chip — the mockup's `.svc` shape with a state tone. Not a
 * fork of Pill: Pill is a dot + word in a fully-rounded pill, these carry an
 * icon and a `ROLE: value` label at --radius-ctl. Both shapes are in the mockup.
 * The icon changes with the tone as well as the colour, so this reads in
 * greyscale (§8).
 */
const CHIP_TONES = {
  ok: 'bg-s-green text-[#046B57] border-bd',
  warn: 'bg-warn-bg text-[#8B5206] border-warn-bd',
  err: 'bg-err-bg text-err border-err-bd',
  neutral: 'bg-s-3 text-t2 border-bd-strong',
};

function StateChip({ tone = 'neutral', icon: Icon, title, children }) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-ctl border px-2 py-0.5',
        'text-[11.5px] font-medium',
        CHIP_TONES[tone] ?? CHIP_TONES.neutral,
      ].join(' ')}
      title={title}
    >
      {Icon && <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />}
      {children}
    </span>
  );
}

function MaintenanceBadge({ maintenance, wpRunning }) {
  if (!wpRunning) return null;
  if (!maintenance) return null;

  if (maintenance.status === 'active') {
    return (
      <StateChip
        tone="warn"
        icon={Wrench}
        title="WordPress maintenance mode is on — visitors see the maintenance page"
      >
        Suspended
      </StateChip>
    );
  }

  if (maintenance.status === 'inactive') {
    return (
      <StateChip tone="ok" icon={CheckCircle2} title="Site is serving normal traffic">
        Live
      </StateChip>
    );
  }

  // Unknown — probe failed (wp-cli error, etc.). Don't alarm; just don't claim a state.
  return (
    <StateChip
      tone="neutral"
      icon={AlertTriangle}
      title={`Maintenance probe failed: ${maintenance.error || 'unknown'}`}
    >
      Maintenance: unknown
    </StateChip>
  );
}

function ContainerBadge({ role, info }) {
  if (!info) return null;

  if (info.available) {
    const running = info.running;
    return (
      <StateChip
        tone={running ? 'ok' : 'err'}
        icon={running ? CheckCircle2 : XCircle}
        title={info.container}
      >
        {role}: {info.status || (running ? 'running' : 'stopped')}
      </StateChip>
    );
  }

  return (
    <StateChip tone="neutral" icon={XCircle} title={info.detail || info.reason}>
      {role}: {info.reason === 'docker_unavailable' ? 'docker unavailable' : 'not found'}
    </StateChip>
  );
}

// ─── Metadata (panel-side, editable) ───────────────────────────────────────

function MetadataCard({ site, onRefetch }) {
  const [mode, setMode] = useState('read');

  if (mode === 'edit') {
    return (
      <EditMetadataForm
        site={site}
        onCancel={() => setMode('read')}
        onSaved={() => {
          setMode('read');
          onRefetch?.();
        }}
      />
    );
  }

  const hasAnyMetadata =
    site.plan ||
    site.subscription_status ||
    site.monthly_price_cents != null ||
    site.billing_day != null ||
    site.panel_notes ||
    site.panel_updated_at;

  return (
    <SectionCard
      title="Metadata"
      icon={Pencil}
      action={
        <button
          type="button"
          onClick={() => setMode('edit')}
          className="inline-flex items-center gap-1.5 text-xs text-t2 hover:text-t1 hover:bg-s-3 px-2 py-1 rounded-md border border-bd"
        >
          <Pencil className="w-3.5 h-3.5" />
          Edit
        </button>
      }
    >
      {!hasAnyMetadata && (
        <p className="text-xs text-t3 italic px-0.5">
          No panel metadata yet. Edit to set plan, billing, and notes.
        </p>
      )}
      {hasAnyMetadata && (
        <>
          <Row label="Plan">
            {site.plan ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-s-3 text-t2 border border-bd">
                {site.plan}
              </span>
            ) : (
              <Muted />
            )}
          </Row>
          <Row label="Subscription">
            <SubscriptionBadge status={site.subscription_status} />
          </Row>
          <Row label="Price">
            <Price cents={site.monthly_price_cents} currency={site.currency} />
          </Row>
          <Row label="Billing day">
            {site.billing_day != null ? `${ordinal(site.billing_day)} of each month` : <Muted />}
          </Row>
          <Row label="Notes">
            {site.panel_notes ? (
              <span className="text-sm text-t2 whitespace-pre-wrap">{site.panel_notes}</span>
            ) : (
              <Muted />
            )}
          </Row>
          <Row label="Updated">
            <DateCell value={site.panel_updated_at} />
          </Row>
        </>
      )}
    </SectionCard>
  );
}

// ─── Metadata edit form ────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUBSCRIPTION_OPTIONS = [
  { value: '', label: '—' },
  { value: 'active', label: 'active' },
  { value: 'past_due', label: 'past_due' },
  { value: 'suspended', label: 'suspended' },
];
const CURRENCY_OPTIONS = ['MAD', 'USD', 'EUR'];

function initialFormFromSite(site) {
  return {
    client_name: site.client_name ?? '',
    admin_email: site.admin_email ?? '',
    plan: site.plan ?? '',
    subscription_status: site.subscription_status ?? '',
    monthly_price: site.monthly_price_cents != null
      ? (site.monthly_price_cents / 100).toFixed(2)
      : '',
    currency: site.currency ?? 'MAD',
    billing_day: site.billing_day != null ? String(site.billing_day) : '',
    notes: site.panel_notes ?? '',
  };
}

function EditMetadataForm({ site, onCancel, onSaved }) {
  const [form, setForm] = useState(() => initialFormFromSite(site));
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const setField = (k) => (e) => {
    const v = typeof e === 'string' ? e : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((fe) => ({ ...fe, [k]: undefined }));
    setFormError(null);
  };

  const handleSave = async () => {
    const body = {};
    const errors = {};

    // Plain string fields — empty → null.
    const initial = initialFormFromSite(site);
    for (const [formKey, patchKey] of [
      ['client_name', 'client_name'],
      ['plan', 'plan'],
      ['subscription_status', 'subscription_status'],
      ['currency', 'currency'],
      ['notes', 'notes'],
    ]) {
      if (form[formKey] !== initial[formKey]) {
        body[patchKey] = form[formKey] === '' ? null : form[formKey];
      }
    }

    // admin_email — validate if non-empty
    if (form.admin_email !== initial.admin_email) {
      if (form.admin_email === '') {
        body.admin_email = null;
      } else if (!EMAIL_RE.test(form.admin_email)) {
        errors.admin_email = 'Invalid email';
      } else {
        body.admin_email = form.admin_email;
      }
    }

    // monthly_price — MAD units → cents
    if (form.monthly_price !== initial.monthly_price) {
      if (form.monthly_price === '') {
        body.monthly_price_cents = null;
      } else {
        const num = parseFloat(form.monthly_price);
        if (!Number.isFinite(num) || num < 0) {
          errors.monthly_price = 'Must be 0 or greater';
        } else {
          body.monthly_price_cents = Math.round(num * 100);
        }
      }
    }

    // billing_day — int 1-28
    if (form.billing_day !== initial.billing_day) {
      if (form.billing_day === '') {
        body.billing_day = null;
      } else {
        const n = parseInt(form.billing_day, 10);
        if (!Number.isInteger(n) || n < 1 || n > 28) {
          errors.billing_day = '1–28';
        } else {
          body.billing_day = n;
        }
      }
    }

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    if (Object.keys(body).length === 0) {
      onCancel();
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      await api(`/api/sites/${encodeURIComponent(site.domain)}`, {
        method: 'PATCH',
        body,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.body?.error) {
        const code = err.body.error;
        if (code.startsWith('invalid_')) {
          const field = code.slice('invalid_'.length);
          const mapped = field === 'monthly_price_cents' ? 'monthly_price' : field;
          setFieldErrors({ [mapped]: 'Server rejected this value' });
        } else {
          setFormError(code);
        }
      } else {
        setFormError(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Metadata — editing"
      icon={Pencil}
      action={
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-xs text-t2 hover:text-t1 hover:bg-s-3 px-2 py-1 rounded-md border border-bd disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-blue hover:bg-blue-deep px-2.5 py-1 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? 'Saving' : 'Save'}
          </button>
        </div>
      }
    >
      {formError && (
        <div className="mb-3 border-l-4 border-l-err bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {formError}
        </div>
      )}

      <FormRow label="Client">
        <TextInput value={form.client_name} onChange={setField('client_name')} disabled={saving} />
      </FormRow>

      <FormRow label="Admin email" error={fieldErrors.admin_email}>
        <TextInput
          type="email"
          value={form.admin_email}
          onChange={setField('admin_email')}
          disabled={saving}
        />
      </FormRow>

      <FormRow label="Plan">
        <TextInput
          value={form.plan}
          onChange={setField('plan')}
          disabled={saving}
          placeholder="basic / pro / enterprise / trial…"
        />
      </FormRow>

      <FormRow label="Subscription">
        <SelectInput
          value={form.subscription_status}
          onChange={setField('subscription_status')}
          disabled={saving}
          options={SUBSCRIPTION_OPTIONS}
        />
      </FormRow>

      <FormRow label="Price" error={fieldErrors.monthly_price}>
        <div className="flex items-center gap-2">
          <TextInput
            type="number"
            step="0.01"
            min="0"
            value={form.monthly_price}
            onChange={setField('monthly_price')}
            disabled={saving}
            className="flex-1"
            placeholder="0.00"
          />
          <SelectInput
            value={form.currency}
            onChange={setField('currency')}
            disabled={saving}
            options={CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }))}
            className="w-24"
          />
        </div>
      </FormRow>

      <FormRow label="Billing day" error={fieldErrors.billing_day}>
        <TextInput
          type="number"
          min="1"
          max="28"
          value={form.billing_day}
          onChange={setField('billing_day')}
          disabled={saving}
          className="w-24"
          placeholder="1–28"
        />
      </FormRow>

      <FormRow label="Notes" alignTop>
        <textarea
          value={form.notes}
          onChange={setField('notes')}
          disabled={saving}
          rows={3}
          className="w-full rounded-lg border border-bd-strong px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue disabled:bg-s-2 disabled:text-t3"
        />
      </FormRow>
    </SectionCard>
  );
}

function FormRow({ label, error, alignTop, children }) {
  return (
    <div className={`flex gap-3 py-1 ${alignTop ? 'items-start' : 'items-center'}`}>
      <span className={`text-xs text-t3 w-28 shrink-0 ${alignTop ? 'pt-2' : ''}`}>{label}</span>
      <div className="flex-1 min-w-0">
        {children}
        {error && <p className="mt-1 text-xs text-err">{error}</p>}
      </div>
    </div>
  );
}

function TextInput({ className = '', ...props }) {
  return (
    <input
      {...props}
      className={`rounded-lg border border-bd-strong px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue disabled:bg-s-2 disabled:text-t3 ${className || 'w-full'}`}
    />
  );
}

function SelectInput({ value, onChange, disabled, options, className = '' }) {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`rounded-lg border border-bd-strong bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue disabled:bg-s-2 disabled:text-t3 ${className}`}
    >
      {options.map((opt) => (
        <option key={opt.value ?? opt} value={opt.value ?? opt}>
          {opt.label ?? opt}
        </option>
      ))}
    </select>
  );
}

function SubscriptionBadge({ status }) {
  if (!status) return <Muted />;
  const styles = {
    active:    'bg-s-green text-ok border-bd',
    past_due:  'bg-warn-bg text-[#8B5206] border-warn-bd',
    suspended: 'bg-err-bg text-err border-err-bd',
  };
  const cls = styles[status] || 'bg-s-3 text-t2 border-bd';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${cls}`}>
      {status}
    </span>
  );
}

function Price({ cents, currency }) {
  if (cents == null) return <Muted />;
  const amount = (cents / 100).toFixed(2);
  return (
    <span className="font-mono text-sm text-t1">
      {amount} <span className="text-xs text-t3">{currency || 'MAD'}</span>
    </span>
  );
}

// ─── Credentials — audited re-reveal ───────────────────────────────────────

function CredentialsCard({ domain }) {
  const [state, setState] = useState({ status: 'hidden' });
  const [launching, setLaunching] = useState(false);

  const reveal = async () => {
    setState({ status: 'loading' });
    try {
      const creds = await api(`/api/sites/${encodeURIComponent(domain)}/credentials`);
      setState({ status: 'shown', creds });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setState({ status: 'not_found' });
      } else {
        setState({ status: 'error', message: err.message });
      }
    }
  };

  const hide = () => setState({ status: 'hidden' });

  const openWpAdmin = async () => {
    const { wp_admin_url, wp_admin_pass } = state.creds || {};
    if (!wp_admin_url || !wp_admin_pass) return;
    setLaunching(true);
    try {
      await navigator.clipboard.writeText(wp_admin_pass);
    } catch {
      // clipboard might fail; still open the URL
    }
    window.open(wp_admin_url, '_blank', 'noopener,noreferrer');
    setTimeout(() => setLaunching(false), 1800);
  };

  return (
    <SectionCard
      title="Credentials"
      icon={ShieldCheck}
      action={
        state.status === 'shown' ? (
          <button
            type="button"
            onClick={hide}
            className="inline-flex items-center gap-1.5 text-xs text-t2 hover:text-t1 hover:bg-s-3 px-2 py-1 rounded-md border border-bd"
          >
            <EyeOff className="w-3.5 h-3.5" />
            Hide
          </button>
        ) : null
      }
    >
      {state.status === 'hidden' && (
        <>
          <p className="text-xs text-t3 mb-3">
            WordPress admin + database credentials, encrypted at rest. Every reveal is audited.
          </p>
          <button
            type="button"
            onClick={reveal}
            className="inline-flex items-center gap-2 rounded-lg bg-blue hover:bg-blue-deep px-3 py-1.5 text-sm font-medium text-white"
          >
            <Eye className="w-4 h-4" />
            Reveal credentials
          </button>
        </>
      )}

      {state.status === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-t3 py-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Fetching credentials…
        </div>
      )}

      {state.status === 'not_found' && (
        <div className="flex items-start gap-2 border-l-4 border-l-warn bg-warn-bg p-3 text-[13px] text-[#8B5206]">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p>No credentials on file for this site.</p>
            <p className="text-xs text-[#8B5206] mt-1">
              The site was provisioned before the panel, or credentials were never
              backfilled. Use <code className="font-mono">npm run upsert-creds</code>{' '}
              to import them.
            </p>
            <button
              type="button"
              onClick={hide}
              className="mt-2 text-xs underline hover:text-[#8B5206]"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {state.status === 'error' && (
        <div className="flex items-start gap-2 border-l-4 border-l-err bg-err-bg p-3 text-[13px] text-err">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Failed to fetch credentials</p>
            <p className="text-xs mt-0.5">{state.message}</p>
          </div>
          <button
            type="button"
            onClick={reveal}
            className="text-xs underline hover:text-err"
          >
            Retry
          </button>
        </div>
      )}

      {state.status === 'shown' && <RevealedCreds creds={state.creds} domain={domain} launching={launching} onOpenWp={openWpAdmin} />}
    </SectionCard>
  );
}

function RevealedCreds({ creds, domain, launching, onOpenWp }) {
  // db_host isn't stored in site_credentials — derive from convention.
  const dbHost = `${domain.replace(/\./g, '_')}_db`;

  return (
    <div className="space-y-4">
      {creds.updated_at && (
        <p className="text-xs text-t3">
          Last updated <DateCell value={creds.updated_at} />
        </p>
      )}

      <CredBlock
        title="WordPress admin"
        items={[
          ...(creds.wp_admin_url ? [{ label: 'Admin URL', value: creds.wp_admin_url, isLink: true }] : []),
          { label: 'Username', value: creds.wp_admin_user },
          { label: 'Password', value: creds.wp_admin_pass, sensitive: true },
        ]}
      />

      <CredBlock
        title="Database"
        items={[
          { label: 'Host', value: dbHost },
          { label: 'Name', value: creds.db_name },
          { label: 'User', value: creds.db_user },
          { label: 'Password', value: creds.db_pass, sensitive: true },
        ]}
      />

      {creds.wp_admin_url && creds.wp_admin_pass && (
        <button
          type="button"
          onClick={onOpenWp}
          disabled={launching}
          className={[
            'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium border',
            launching
              ? 'bg-s-green border-bd text-ok'
              : 'bg-white border-bd-strong text-t2 hover:bg-s-2',
          ].join(' ')}
        >
          {launching ? (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Password copied — opening WP admin
            </>
          ) : (
            <>
              <ExternalLink className="w-4 h-4" />
              Open WP admin (auto-copy password)
            </>
          )}
        </button>
      )}

      {creds.notes && (
        <p className="text-xs text-t3 italic">
          Notes: <span className="text-t2">{creds.notes}</span>
        </p>
      )}
    </div>
  );
}

// ─── Backups — list, create, download ──────────────────────────────────────

function BackupsCard({ domain }) {
  const [list, setList] = useState({ status: 'loading' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    setList({ status: 'loading' });
    try {
      const data = await api(`/api/sites/${encodeURIComponent(domain)}/backups`);
      setList({ status: 'ok', data });
    } catch (err) {
      setList({ status: 'error', message: err.message });
    }
  };

  useEffect(() => { load(); }, [domain]);

  const runCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      await api(`/api/sites/${encodeURIComponent(domain)}/backups`, { method: 'POST', body: {} });
      await load();
    } catch (err) {
      const detail = err instanceof ApiError
        ? (err.body?.body?.detail || err.body?.detail || err.message)
        : err.message;
      setCreateError(detail);
    } finally {
      setCreating(false);
    }
  };

  return (
    <SectionCard
      title="Backups"
      icon={Archive}
      action={
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs text-t2 hover:text-t1 hover:bg-s-3 px-2 py-1 rounded-md border border-bd"
            title="Upload a .tar.gz backup from your machine"
          >
            <Upload className="w-3.5 h-3.5" />
            Import
          </button>
          <button
            type="button"
            onClick={runCreate}
            disabled={creating}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-blue hover:bg-blue-deep px-2.5 py-1 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {creating
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Archive className="w-3.5 h-3.5" />}
            {creating ? 'Creating…' : 'Create backup'}
          </button>
        </div>
      }
    >
      <p className="text-xs text-t3 mb-3">
        Full snapshot: site directory (docker-compose + WordPress files) + a fresh
        mysqldump. Takes 30–180 s depending on site size.
      </p>

      {createError && (
        <div className="mb-3 border-l-4 border-l-err bg-err-bg px-3 py-2 text-[12.5px] text-err">
          Failed to create backup: {createError}
        </div>
      )}

      {list.status === 'loading' && (
        <div className="text-xs text-t3 py-2 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading backups…
        </div>
      )}

      {list.status === 'error' && (
        <div className="border-l-4 border-l-err bg-err-bg px-3 py-2 text-[12.5px] text-err">
          Failed to load backups: {list.message}
        </div>
      )}

      {list.status === 'ok' && list.data.count === 0 && (
        <p className="text-xs text-t3 italic px-0.5">
          No backups yet. Click <strong>Create backup</strong> to take the first snapshot.
        </p>
      )}

      {list.status === 'ok' && list.data.count > 0 && (
        <BackupsTable domain={domain} backups={list.data.backups} />
      )}
      {importOpen && (
        <ImportBackupModal
          domain={domain}
          onClose={() => setImportOpen(false)}
          onImported={async () => { setImportOpen(false); await load(); }}
        />
      )}
    </SectionCard>
  );
}

function ImportBackupModal({ domain, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);    // 0-100
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleUpload = () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    setProgress(0);

    // XHR (not fetch) so we can show upload progress. fetch() doesn't expose
    // upload progress events yet. Backend accepts multipart/form-data —
    // browser sets the Content-Type + boundary automatically when we pass
    // a FormData body.
    const form = new FormData();
    form.append('backup', file, file.name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/sites/${encodeURIComponent(domain)}/backups/import`);
    xhr.withCredentials = true;
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener('load', () => {
      setUploading(false);
      try {
        const body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        if (xhr.status >= 200 && xhr.status < 300) {
          setResult(body);
        } else {
          setError({
            status: xhr.status,
            code: body?.error || `http_${xhr.status}`,
            detail: body?.body?.detail || body?.detail || xhr.statusText,
          });
        }
      } catch {
        setError({ status: xhr.status, code: 'parse_error', detail: 'invalid response' });
      }
    });
    xhr.addEventListener('error', () => {
      setUploading(false);
      setError({ code: 'network_error', detail: 'upload failed (network)' });
    });
    xhr.addEventListener('abort', () => {
      setUploading(false);
      setError({ code: 'aborted', detail: 'upload aborted' });
    });

    xhr.send(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-t1/50 p-4">
      <div className="bg-white rounded-xl border border-bd shadow-xl max-w-xl w-full">
        <div className="px-5 py-3 flex items-center gap-2 border-b border-bd bg-s-2">
          <Upload className="w-4 h-4 text-t2" />
          <h2 className="text-sm font-semibold text-t1 flex-1">
            Import backup · <code className="font-mono">{domain}</code>
          </h2>
          {!uploading && (
            <button type="button" onClick={onClose} className="text-t2 hover:bg-s-3 rounded p-0.5" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-5">
          {!result && (
            <>
              <p className="text-sm text-t2 mb-3">
                Upload a <code className="font-mono">.tar.gz</code> archive previously
                exported from another Webance site or received from a client.
              </p>
              <p className="text-xs text-t3 mb-4">
                Expected layout: <code className="font-mono">site_dir/ + database.sql + meta.json</code>
                {' '}(+ <code className="font-mono">wp_files/</code> for v2 archives). Max 2 GB.
                Lands under <code className="font-mono">/opt/webance/imported-backups/</code>{' '}
                on VPS 1 — not subject to count-based retention.
              </p>

              <label className="block mb-4">
                <span className="block text-xs text-t3 mb-1">Archive file</span>
                <input
                  type="file"
                  accept=".tar.gz,.tgz,application/gzip"
                  disabled={uploading}
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-t2 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-bd-strong file:bg-white file:text-t2 hover:file:bg-s-2 file:cursor-pointer disabled:opacity-50"
                />
                {file && (
                  <span className="block mt-1 text-xs text-t3">
                    {file.name} · {formatBytes(file.size)}
                  </span>
                )}
              </label>

              {uploading && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-xs text-t2 mb-1">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Uploading… {progress}%
                  </div>
                  <div className="h-1.5 bg-s-3 rounded overflow-hidden">
                    <div className="h-full bg-blue transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-4 border-l-4 border-l-err bg-err-bg p-3 text-[13px] text-err">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium">
                        Import failed
                        {error.status ? <> · <span className="font-mono">{error.status}</span></> : null}
                      </p>
                      <p className="text-xs mt-0.5 font-mono break-all">{error.code}</p>
                      {error.detail && <p className="text-xs mt-0.5 break-all">{error.detail}</p>}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={uploading}
                  className="rounded-lg border border-bd-strong bg-white hover:bg-s-2 px-3 py-1.5 text-sm text-t2 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue hover:bg-blue-deep px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
              </div>
            </>
          )}

          {result && (
            <>
              <div className="mb-4 flex items-start gap-2 border-l-4 border-l-ok bg-s-green p-3 text-[13px] text-ok">
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold">Imported successfully</p>
                  <p className="text-xs mt-0.5">
                    <code className="font-mono">{result.filename}</code> · {formatBytes(result.size_bytes)}
                    {' '}· layout v{result.layout_version}
                  </p>
                </div>
              </div>
              <p className="text-xs text-t3 mb-4">
                The imported backup is now available in the list below. Click{' '}
                <strong>Restore</strong> on its row to use it.
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onImported}
                  className="rounded-lg bg-blue hover:bg-blue-deep px-3 py-1.5 text-sm font-medium text-white"
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BackupsTable({ domain, backups }) {
  const [restoreTarget, setRestoreTarget] = useState(null);   // filename or null
  return (
    <>
      <div className="rounded-lg border border-bd overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd bg-s-2">
              <th className="px-3 py-2 font-medium">Created</th>
              <th className="px-3 py-2 font-medium">Size</th>
              <th className="px-3 py-2 font-medium">Offsite</th>
              <th className="px-3 py-2 font-medium">Filename</th>
              <th className="px-3 py-2 font-medium w-44 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-bd">
            {backups.map((b) => (
              <tr key={b.filename} className="hover:bg-s-2">
                <td className="px-3 py-2 text-xs text-t2 whitespace-nowrap">
                  <DateCell value={b.created_at} />
                </td>
                <td className="px-3 py-2 text-xs font-mono text-t2 whitespace-nowrap">
                  {formatBytes(b.size_bytes)}
                </td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                  <OffsiteBadge at={b.offsite_at} />
                </td>
                <td className="px-3 py-2 text-xs font-mono text-t3 break-all">
                  {b.imported && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 mr-1.5 rounded-md text-[10px] font-medium bg-s-3 text-t2 border border-bd"
                      title="Uploaded manually — not subject to retention pruning"
                    >
                      <Upload className="w-3 h-3" />
                      imported
                    </span>
                  )}
                  {b.filename}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1">
                    <a
                      href={`/api/sites/${encodeURIComponent(domain)}/backups/${encodeURIComponent(b.filename)}/download`}
                      className="inline-flex items-center gap-1 text-xs text-t2 hover:text-t1 hover:bg-s-3 px-2 py-1 rounded-md border border-bd"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </a>
                    <button
                      type="button"
                      onClick={() => setRestoreTarget(b.filename)}
                      className="inline-flex items-center gap-1.5 rounded-ctl border border-bd-strong bg-white px-2 py-1 text-[12px] font-medium text-t1 transition-colors hover:bg-s-3"
                      title="Restore this backup — destructive"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Restore
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {restoreTarget && (
        <RestoreSiteModal
          domain={domain}
          sourceFilename={restoreTarget}
          onClose={() => setRestoreTarget(null)}
        />
      )}
    </>
  );
}

function OffsiteBadge({ at }) {
  if (at) {
    const date = new Date(at);
    const label = Number.isNaN(date.getTime()) ? at : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    return (
      <span
        title={`Replicated to VPS 2 at ${at}`}
        className="inline-flex items-center gap-1 rounded-ctl border border-bd bg-s-green px-1.5 py-0.5 text-[11.5px] font-medium text-ok"
      >
        <CheckCircle2 className="w-3 h-3" />
        {label}
      </span>
    );
  }
  return (
    <span
      title="Not yet replicated to VPS 2 (pending, or offsite not configured)"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium bg-s-3 text-t3 border border-bd"
    >
      <XCircle className="w-3 h-3" />
      —
    </span>
  );
}

function formatBytes(n) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// ─── Actions — reachability check + delete modal ───────────────────────────

function ActionsCard({ domain, maintenance, wpRunning, onRefetch }) {
  const [reach, setReach] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [maintModal, setMaintModal] = useState(null);  // 'suspend' | 'resume' | null

  const runCheck = async () => {
    setReach({ status: 'checking' });
    try {
      const data = await api(`/api/sites/${encodeURIComponent(domain)}/reachability`);
      setReach({ status: 'ok', data });
    } catch (err) {
      setReach({ status: 'failed', message: err.message });
    }
  };

  // Maintenance button visibility:
  //   - WP container must be running (else wp-cli has nothing to talk to)
  //   - probe must have returned a known status (active/inactive). On probe
  //     error we surface the "Maintenance: unknown" badge in Overview but
  //     hide the button to avoid actions on an unclear state.
  const showSuspend = wpRunning && maintenance?.status === 'inactive';
  const showResume  = wpRunning && maintenance?.status === 'active';

  return (
    <SectionCard title="Actions" icon={RefreshCw}>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={runCheck}
          disabled={reach?.status === 'checking'}
          className="inline-flex items-center gap-2 rounded-lg border border-bd-strong bg-white hover:bg-s-2 px-3 py-1.5 text-sm text-t2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {reach?.status === 'checking'
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <RefreshCw className="w-4 h-4" />}
          Check reachability
        </button>
        {showSuspend && (
          <button
            type="button"
            onClick={() => setMaintModal('suspend')}
            className="inline-flex items-center gap-[7px] rounded-ctl border border-bd-strong bg-white px-3.5 py-2 text-[13px] font-medium leading-none text-t1 transition-colors hover:bg-s-3"
            title="Put up WP maintenance page — site stays installed, reversible"
          >
            <Power className="w-4 h-4" />
            Suspend site
          </button>
        )}
        {showResume && (
          <button
            type="button"
            onClick={() => setMaintModal('resume')}
            className="inline-flex items-center gap-[7px] rounded-ctl border border-bd-strong bg-white px-3.5 py-2 text-[13px] font-medium leading-none text-t1 transition-colors hover:bg-s-3"
            title="Take down maintenance page — restore normal traffic"
          >
            <Play className="w-4 h-4" />
            Resume site
          </button>
        )}
        <button
          type="button"
          onClick={() => setDeleteOpen(true)}
          className="inline-flex items-center gap-[7px] rounded-ctl border border-err-bd bg-white px-3.5 py-2 text-[13px] font-medium leading-none text-err transition-colors hover:bg-err-bg"
        >
          <Trash2 className="w-4 h-4" />
          Delete site
        </button>
      </div>

      {reach?.status === 'ok' && <ReachabilityResult data={reach.data} domain={domain} />}
      {reach?.status === 'failed' && (
        <div className="mt-3 flex items-start gap-2 border-l-4 border-l-err bg-err-bg p-3 text-[13px] text-err">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Check failed to run</p>
            <p className="text-xs mt-0.5">{reach.message}</p>
          </div>
        </div>
      )}

      {maintModal && (
        <MaintenanceModal
          domain={domain}
          mode={maintModal}
          onClose={() => setMaintModal(null)}
          onDone={() => { setMaintModal(null); onRefetch?.(); }}
        />
      )}
      {deleteOpen && <DeleteSiteModal domain={domain} onClose={() => setDeleteOpen(false)} />}
    </SectionCard>
  );
}

// ─── Suspend / Resume — simple confirm modal ───────────────────────────────

function MaintenanceModal({ domain, mode, onClose, onDone }) {
  const [step, setStep] = useState('confirm');         // confirm → running → done | failed
  const [error, setError] = useState(null);

  const isSuspend = mode === 'suspend';
  const verb = isSuspend ? 'Suspend' : 'Resume';
  const route = isSuspend ? 'suspend' : 'resume';

  // Tailwind JIT scans for literal class strings — dynamic interpolation
  // (`bg-${tone}-50`) would be silently dropped. Keep the full class names
  // visible to the scanner.
  const headerCls = isSuspend
    ? 'flex items-center gap-2 border-b border-bd bg-warn-bg px-5 py-3'
    : 'flex items-center gap-2 border-b border-bd bg-s-green px-5 py-3';
  const iconCls = isSuspend ? 'w-4 h-4 text-warn' : 'w-4 h-4 text-ok';
  const titleCls = isSuspend
    ? 'text-sm font-semibold text-[#8B5206] flex-1'
    : 'text-sm font-semibold text-ok flex-1';
  const closeBtnCls = isSuspend
    ? 'text-warn hover:bg-warn-bg rounded p-0.5'
    : 'text-ok hover:bg-s-green rounded p-0.5';

  const run = async () => {
    setStep('running');
    setError(null);
    try {
      await api(`/api/sites/${encodeURIComponent(domain)}/${route}`, {
        method: 'POST',
        body: {},
      });
      setStep('done');
      setTimeout(() => onDone(), 1200);
    } catch (err) {
      setError(err instanceof ApiError
        ? { code: err.body?.error || `http_${err.status}`,
            detail: err.body?.detail || err.body?.body?.detail || err.message,
            upstream_error: err.body?.body?.error || null }
        : { code: 'network_error', detail: err.message });
      setStep('failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-t1/50 p-4">
      <div className="bg-white rounded-xl border border-bd shadow-xl max-w-md w-full">
        <div className={headerCls}>
          {isSuspend ? <Power className={iconCls} /> : <Play className={iconCls} />}
          <h2 className={titleCls}>
            {verb} site · <code className="font-mono">{domain}</code>
          </h2>
          {step !== 'running' && (
            <button
              type="button"
              onClick={onClose}
              className={closeBtnCls}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-5 text-sm text-t2">
          {step === 'confirm' && (
            <>
              {isSuspend ? (
                <>
                  <p>
                    Visitors of <code className="font-mono">{domain}</code> will see
                    WordPress's default <em>"Briefly unavailable for scheduled
                    maintenance"</em> page until you resume.
                  </p>
                  <p className="mt-2 text-xs text-t3">
                    The site, database, and files stay intact — this is reversible
                    in one click. Use for past-due customers, upgrades, or
                    emergency takedown.
                  </p>
                </>
              ) : (
                <p>
                  Bring <code className="font-mono">{domain}</code> back online.
                  Visitors will see the site normally as soon as WordPress
                  removes the <code className="font-mono">.maintenance</code> file
                  (immediate).
                </p>
              )}
              <div className="mt-5 flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 text-sm text-t2 hover:bg-s-3 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={run}
                  className={
                    isSuspend
                      ? 'inline-flex items-center gap-[7px] rounded-ctl bg-warn px-3.5 py-2 text-[13px] font-medium leading-none text-white transition-colors hover:brightness-110'
                      : 'px-3 py-1.5 text-sm font-medium text-white bg-blue hover:bg-blue-deep rounded-md inline-flex items-center gap-2'
                  }
                >
                  {isSuspend ? <Power className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {verb} site
                </button>
              </div>
            </>
          )}

          {step === 'running' && (
            <div className="flex items-center gap-2 text-t2 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {isSuspend ? 'Activating maintenance mode…' : 'Deactivating maintenance mode…'}
            </div>
          )}

          {step === 'done' && (
            <div className="flex items-center gap-2 text-ok py-2">
              <CheckCircle2 className="w-5 h-5" />
              {isSuspend ? 'Site suspended.' : 'Site resumed.'}
            </div>
          )}

          {step === 'failed' && (
            <div>
              <div className="flex items-start gap-2 text-err">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium">{verb} failed</p>
                  <dl className="mt-1 text-xs grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                    <dt className="font-medium">Code</dt>
                    <dd className="font-mono">{error?.upstream_error || error?.code || 'unknown'}</dd>
                    {error?.detail && (
                      <>
                        <dt className="font-medium">Detail</dt>
                        <dd className="break-words">{error.detail}</dd>
                      </>
                    )}
                  </dl>
                </div>
              </div>
              <div className="mt-4 flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 text-sm text-t2 hover:bg-s-3 rounded-md"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => setStep('confirm')}
                  className="px-3 py-1.5 text-sm font-medium text-t2 border border-bd-strong hover:bg-s-2 rounded-md"
                >
                  Try again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Delete site — 2-step confirm modal ────────────────────────────────────

function DeleteSiteModal({ domain, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('warning');     // warning → confirm → running → done | failed
  const [dryRun, setDryRun] = useState(true);
  // "Also remove its DNS records": only this server's A/AAAA of the site's names.
  const cleanup = useDnsPreview({ path: `/api/sites/${encodeURIComponent(domain)}/dns-cleanup`, delay: 0 });
  const [removeDns, setRemoveDns] = useState(false);
  const dnsCleanup = removeDns && !dryRun && cleanup.preview?.can_apply ? { fingerprint: cleanup.preview.fingerprint } : null;
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canDelete = typed === domain;
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const runDelete = async () => {
    setStep('running');
    setError(null);
    setResult(null);
    const noAnswer = {
      code: 'no_answer',
      detail: 'No answer from the server — check Audit before retrying.',
    };
    let jobId;
    try {
      const url = `/api/sites/${encodeURIComponent(domain)}${dryRun ? '?dry_run=true' : ''}`;
      const started = await api(url, {
        method: 'DELETE',
        body: { confirm_domain: domain, ...(dnsCleanup ? { dns_cleanup: dnsCleanup } : {}) },
      });
      jobId = started.job_id;
    } catch (err) {
      // The request itself was refused (400/503/…) or never got through.
      setError(err instanceof ApiError && err.body?.error
        ? { code: err.body.error, detail: err.body.detail || err.message }
        : noAnswer);
      setStep('failed');
      return;
    }

    // Poll the job; tolerate a few transient poll failures (panel restart).
    let misses = 0;
    for (;;) {
      await new Promise((r) => setTimeout(r, 2500));
      if (!aliveRef.current) return;
      let job;
      try {
        job = await api(`/api/sites/jobs/${jobId}`);
        misses = 0;
      } catch {
        if (++misses >= 8) { setError(noAnswer); setStep('failed'); return; }
        continue;
      }
      if (job.status === 'done') {
        setResult(job.result);
        const ok = job.result?.ok === true;
        setStep(ok ? 'done' : 'failed');
        if (ok && !dryRun) setTimeout(() => navigate('/sites'), job.result?.dns ? 6000 : 2500);
        return;
      }
      if (job.status === 'failed') {
        setResult(job.error);
        setError({ code: job.error?.code, detail: job.error?.detail, upstream_status: job.error?.upstream_status });
        setStep('failed');
        return;
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-t1/50 p-4">
      <div className="bg-white rounded-xl border border-bd shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 border-b border-bd bg-err-bg px-5 py-3">
          <Trash2 className="w-4 h-4 text-err" />
          <h2 className="text-sm font-semibold text-err flex-1">
            Delete site · <code className="font-mono">{domain}</code>
          </h2>
          {step !== 'running' && (
            <button
              type="button"
              onClick={onClose}
              className="text-err hover:bg-err-bg rounded p-0.5"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-5">
          {step === 'warning' && (
            <WarningStep
              domain={domain}
              dryRun={dryRun}
              setDryRun={setDryRun}
              onCancel={onClose}
              onNext={() => setStep('confirm')}
              cleanup={cleanup}
              removeDns={removeDns}
              setRemoveDns={setRemoveDns}
            />
          )}
          {step === 'confirm' && (
            <ConfirmStep
              domain={domain}
              dryRun={dryRun}
              typed={typed}
              setTyped={setTyped}
              canDelete={canDelete}
              onBack={() => setStep('warning')}
              onDelete={runDelete}
            />
          )}
          {step === 'running' && <RunningStep dryRun={dryRun} />}
          {step === 'done' && <ResultStep result={result} dryRun={dryRun} onClose={onClose} />}
          {step === 'failed' && (
            <FailedStep
              result={result}
              error={error}
              dryRun={dryRun}
              onRetry={() => setStep('confirm')}
              onClose={onClose}
            />
          )}
          {(step === 'done' || step === 'failed') && result?.dns && (
            <div className="mt-3"><DnsOutcome dns={result.dns} /></div>
          )}
        </div>
      </div>
    </div>
  );
}

function WarningStep({ domain, dryRun, setDryRun, onCancel, onNext, cleanup, removeDns, setRemoveDns }) {
  return (
    <>
      <div className="mb-4 border-l-4 border-l-err bg-err-bg p-3 text-[13px] text-err">
        <p className="font-semibold mb-1">This is irreversible.</p>
        <p className="text-xs">
          All data for <code className="font-mono">{domain}</code> will be destroyed.
        </p>
      </div>

      <p className="text-sm text-t2 mb-2">The following steps will run on VPS 1:</p>
      <ol className="text-sm text-t2 ml-5 mb-4 space-y-1 list-decimal">
        <li>Dump the database to <code className="font-mono text-xs">/opt/webance/deleted-sites/</code> (safety net)</li>
        <li><strong>Remove containers + volumes</strong> (<code className="font-mono text-xs">docker compose down -v</code>)</li>
        <li>Delete the site directory</li>
        <li>Remove the row from VPS 1's <code className="font-mono text-xs">sites.db</code></li>
        <li>Remove its Apache vhosts, then its Let's Encrypt certificate (DNS is not touched unless ticked below)</li>
      </ol>
      <p className="text-sm text-t2 mb-4">
        Then the panel cascades: deletes the encrypted credentials + metadata rows.
      </p>

      <div className="mb-4 rounded-lg border border-bd p-3 space-y-2">
        {cleanup.preview?.can_apply ? (
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={removeDns} onChange={(e) => setRemoveDns(e.target.checked)} className="mt-0.5" />
            <span>
              <span className="font-medium text-t1">Also remove its DNS records</span>
              <span className="block text-xs text-t3">Only the records below — they point at this server. Removed after the site is gone{dryRun ? '; not in a dry run' : ''}.</span>
            </span>
          </label>
        ) : null}
        <DnsPreview {...cleanup} title="DNS" />
      </div>

      <label className="flex items-start gap-2 rounded-lg border border-bd bg-s-2 p-3 cursor-pointer mb-4">
        <input
          type="checkbox"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
          className="mt-0.5"
        />
        <div className="text-sm">
          <div className="font-medium text-t1">Run as dry-run first</div>
          <div className="text-xs text-t3 mt-0.5">
            No containers removed, no files deleted. Logs what would happen. Uncheck to perform the real teardown.
          </div>
        </div>
      </label>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-bd-strong bg-white hover:bg-s-2 px-3 py-1.5 text-sm text-t2"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onNext}
          className="rounded-ctl bg-err px-3.5 py-2 text-[13px] font-medium leading-none text-white transition-colors hover:brightness-110"
        >
          Next →
        </button>
      </div>
    </>
  );
}

function ConfirmStep({ domain, dryRun, typed, setTyped, canDelete, onBack, onDelete }) {
  return (
    <>
      <p className="text-sm text-t2 mb-2">
        Mode: <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${dryRun ? 'bg-s-3 text-t2 border-bd' : 'bg-err-bg text-err border-err-bd'}`}>
          {dryRun ? 'Dry-run (safe)' : 'REAL DELETE'}
        </span>
      </p>
      <p className="text-sm text-t2 mb-3">
        Type the domain <code className="font-mono text-t1">{domain}</code> to confirm.
      </p>

      <input
        type="text"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={domain}
        autoFocus
        className="w-full rounded-lg border border-bd-strong px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue focus:border-err mb-4"
      />

      <div className="flex justify-between gap-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-bd-strong bg-white hover:bg-s-2 px-3 py-1.5 text-sm text-t2"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={!canDelete}
          className={[
            'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium',
            canDelete
              ? (dryRun ? 'bg-blue hover:bg-blue-deep text-white' : 'bg-err text-white hover:brightness-110')
              : 'bg-s-3 text-t3 cursor-not-allowed',
          ].join(' ')}
        >
          <Trash2 className="w-4 h-4" />
          {dryRun ? 'Run dry-run' : 'Delete site'}
        </button>
      </div>
    </>
  );
}

function RunningStep({ dryRun }) {
  return (
    <div className="flex items-center gap-3 py-6 text-t2">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-sm">
        {dryRun ? 'Running dry-run…' : 'Tearing down site…'} Can take 30–90 s.
      </span>
    </div>
  );
}

function ResultStep({ result, dryRun, onClose }) {
  const cascade = result?.panel_cascade;
  return (
    <>
      <div className="mb-4 flex items-start gap-2 border-l-4 border-l-ok bg-s-green p-3 text-[13px] text-ok">
        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">
            {dryRun ? 'Dry-run complete — nothing destroyed.' : 'Site deleted.'}
          </p>
          <p className="text-xs mt-0.5">
            {dryRun
              ? `Panel cascade would be: ${cascade}. Review the steps below, then run without dry-run.`
              : `Panel cascade: ${cascade}. Redirecting to sites list…`}
          </p>
        </div>
      </div>

      <StepsTable steps={result?.steps || []} />

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg bg-blue hover:bg-blue-deep px-3 py-1.5 text-sm font-medium text-white"
        >
          Close
        </button>
      </div>
    </>
  );
}

function FailedStep({ result, error, dryRun, onRetry, onClose }) {
  return (
    <>
      <div className="mb-4 flex items-start gap-2 border-l-4 border-l-err bg-err-bg p-3 text-[13px] text-err">
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold">{dryRun ? 'Dry-run failed' : 'Delete failed'}</p>
          {error && (
            <p className="text-xs mt-0.5 break-all">
              <code className="font-mono">{error.code}</code>
              {error.upstream_status ? <> · upstream {error.upstream_status}</> : null}
              {error.detail ? <>: {error.detail}</> : null}
            </p>
          )}
          {result?.panel_cascade === 'done' ? (
            <p className="text-xs mt-1 text-err">
              The site and its panel rows are gone; a cleanup step after that failed — see the table.
              Do not retry the delete: fix the failed step by hand.
            </p>
          ) : (result?.panel_cascade || error?.code !== 'no_answer') && (
            <p className="text-xs mt-1 text-err">
              Panel cascade: <code className="font-mono">{result?.panel_cascade ?? 'skipped'}</code>.
              Encrypted credentials + metadata preserved — investigate before retrying.
            </p>
          )}
        </div>
      </div>

      {result?.steps?.length > 0 && <StepsTable steps={result.steps} />}

      <div className="mt-4 flex justify-between gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-bd-strong bg-white hover:bg-s-2 px-3 py-1.5 text-sm text-t2"
        >
          Close
        </button>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-ctl bg-err px-3.5 py-2 text-[13px] font-medium leading-none text-white transition-colors hover:brightness-110"
        >
          Retry
        </button>
      </div>
    </>
  );
}

function StepsTable({ steps }) {
  return (
    <div className="rounded-lg border border-bd overflow-hidden text-xs">
      <table className="w-full">
        <thead>
          <tr className="bg-s-2 border-b border-bd text-t3 uppercase tracking-wide">
            <th className="px-3 py-1.5 text-left font-medium w-32">Step</th>
            <th className="px-3 py-1.5 text-left font-medium w-20">Status</th>
            <th className="px-3 py-1.5 text-left font-medium">Detail</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-bd">
          {steps.map((s, i) => (
            <tr key={i} className={s.skipped ? 'text-t3' : 'text-t2'}>
              <td className="px-3 py-1.5 font-mono">{s.step}</td>
              <td className="px-3 py-1.5">
                {s.skipped ? 'skipped' : s.ok ? <span className="text-ok">ok</span> : <span className="text-err">fail</span>}
              </td>
              <td className="px-3 py-1.5 font-mono break-all">
                {s.note || s.message
                  || (s.removed?.length ? `removed ${s.removed.join(' + ')}` : '')
                  || (s.certificate ? `certificate: ${s.certificate}` : '')
                  || (s.rows_affected != null ? `rows: ${s.rows_affected}` : '')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Restore site — 2-step confirm modal ───────────────────────────────────

function RestoreSiteModal({ domain, sourceFilename, onClose }) {
  const [step, setStep] = useState('warning');     // warning → confirm → running → done | failed
  const [dryRun, setDryRun] = useState(true);
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canRestore = typed === domain;

  const runRestore = async () => {
    setStep('running');
    setError(null);
    try {
      const url = `/api/sites/${encodeURIComponent(domain)}/restore${dryRun ? '?dry_run=true' : ''}`;
      const data = await api(url, {
        method: 'POST',
        body: { source_filename: sourceFilename, confirm_domain: domain },
      });
      setResult(data);
      setStep(data.ok ? 'done' : 'failed');
    } catch (err) {
      setError(err instanceof ApiError
        ? { code: err.body?.error || `http_${err.status}`, detail: err.body?.detail || err.message, upstream_status: err.body?.upstream_status }
        : { code: 'network_error', detail: err.message });
      setStep('failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-t1/50 p-4">
      <div className="bg-white rounded-xl border border-bd shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 border-b border-bd bg-warn-bg px-5 py-3">
          <RotateCcw className="w-4 h-4 text-[#8B5206]" />
          <h2 className="text-sm font-semibold text-[#8B5206] flex-1">
            Restore · <code className="font-mono">{domain}</code>
          </h2>
          {step !== 'running' && (
            <button type="button" onClick={onClose} className="text-[#8B5206] hover:bg-warn-bg rounded p-0.5" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-5">
          {step === 'warning' && (
            <RestoreWarningStep
              domain={domain}
              sourceFilename={sourceFilename}
              dryRun={dryRun}
              setDryRun={setDryRun}
              onCancel={onClose}
              onNext={() => setStep('confirm')}
            />
          )}
          {step === 'confirm' && (
            <RestoreConfirmStep
              domain={domain}
              sourceFilename={sourceFilename}
              dryRun={dryRun}
              typed={typed}
              setTyped={setTyped}
              canRestore={canRestore}
              onBack={() => setStep('warning')}
              onRestore={runRestore}
            />
          )}
          {step === 'running' && <RestoreRunningStep dryRun={dryRun} />}
          {step === 'done' && <RestoreResultStep result={result} dryRun={dryRun} onClose={onClose} />}
          {step === 'failed' && (
            <RestoreFailedStep
              result={result}
              error={error}
              dryRun={dryRun}
              onRetry={() => setStep('confirm')}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function RestoreWarningStep({ domain, sourceFilename, dryRun, setDryRun, onCancel, onNext }) {
  return (
    <>
      <div className="mb-4 border-l-4 border-l-warn bg-warn-bg p-3 text-[13px] text-[#8B5206]">
        <p className="font-semibold mb-1">This will replace the current site.</p>
        <p className="text-xs">
          All current data for <code className="font-mono">{domain}</code> will be replaced
          by the contents of{' '}
          <code className="font-mono break-all">{sourceFilename}</code>.
        </p>
      </div>

      <p className="text-sm text-t2 mb-2">Orchestrator steps (on VPS 1):</p>
      <ol className="text-sm text-t2 ml-5 mb-4 space-y-1 list-decimal">
        <li>Acquire per-site lock (blocks concurrent backups)</li>
        <li><strong>Safety backup</strong> of current state → <code className="font-mono text-xs">/opt/webance/pre-restore-backups/</code></li>
        <li>Extract the source tarball to scratch, verify layout</li>
        <li><strong>Stop + remove</strong> current containers + volumes (<code className="font-mono text-xs">docker compose down -v</code>)</li>
        <li>Replace <code className="font-mono text-xs">site_dir</code> contents with the backup's</li>
        <li>Start containers (<code className="font-mono text-xs">docker compose up -d</code>)</li>
        <li>Wait for the db container to accept connections</li>
        <li>Import <code className="font-mono text-xs">database.sql</code> into the db container</li>
      </ol>
      <p className="text-xs text-t3 mb-4">
        If any destructive step fails, the orchestrator will attempt to roll back from
        the safety backup automatically.
      </p>

      <label className="flex items-start gap-2 rounded-lg border border-bd bg-s-2 p-3 cursor-pointer mb-4">
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="mt-0.5" />
        <div className="text-sm">
          <div className="font-medium text-t1">Run as dry-run first</div>
          <div className="text-xs text-t3 mt-0.5">
            Validates source + lock + lookup. No containers touched, no files moved.
          </div>
        </div>
      </label>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-bd-strong bg-white hover:bg-s-2 px-3 py-1.5 text-sm text-t2">
          Cancel
        </button>
        <button type="button" onClick={onNext} className="rounded-ctl bg-warn px-3.5 py-2 text-[13px] font-medium leading-none text-white transition-colors hover:brightness-110">
          Next →
        </button>
      </div>
    </>
  );
}

function RestoreConfirmStep({ domain, sourceFilename, dryRun, typed, setTyped, canRestore, onBack, onRestore }) {
  return (
    <>
      <p className="text-sm text-t2 mb-2">
        Mode:{' '}
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${dryRun ? 'bg-s-3 text-t2 border-bd' : 'bg-warn-bg text-[#8B5206] border-warn-bd'}`}>
          {dryRun ? 'Dry-run (safe)' : 'REAL RESTORE'}
        </span>
      </p>
      <p className="text-sm text-t2 mb-1">
        Source: <code className="font-mono text-xs break-all">{sourceFilename}</code>
      </p>
      <p className="text-sm text-t2 mb-3">
        Type the domain <code className="font-mono text-t1">{domain}</code> to confirm.
      </p>

      <input
        type="text"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={domain}
        autoFocus
        className="w-full rounded-lg border border-bd-strong px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue mb-4"
      />

      <div className="flex justify-between gap-2">
        <button type="button" onClick={onBack} className="rounded-lg border border-bd-strong bg-white hover:bg-s-2 px-3 py-1.5 text-sm text-t2">
          ← Back
        </button>
        <button
          type="button"
          onClick={onRestore}
          disabled={!canRestore}
          className={[
            'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium',
            canRestore
              ? (dryRun ? 'bg-blue hover:bg-blue-deep text-white' : 'bg-warn text-white hover:brightness-110')
              : 'bg-s-3 text-t3 cursor-not-allowed',
          ].join(' ')}
        >
          <RotateCcw className="w-4 h-4" />
          {dryRun ? 'Run dry-run' : 'Restore site'}
        </button>
      </div>
    </>
  );
}

function RestoreRunningStep({ dryRun }) {
  return (
    <div className="flex items-center gap-3 py-6 text-t2">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-sm">
        {dryRun ? 'Running dry-run…' : 'Restoring site…'} Can take 1-5 min (safety backup + extract + compose + import).
      </span>
    </div>
  );
}

function RestoreResultStep({ result, dryRun, onClose }) {
  return (
    <>
      <div className="mb-4 flex items-start gap-2 border-l-4 border-l-ok bg-s-green p-3 text-[13px] text-ok">
        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">
            {dryRun ? 'Dry-run complete — nothing changed.' : 'Site restored.'}
          </p>
          {result?.safety_backup && (
            <p className="text-xs mt-0.5">
              Safety backup taken: <code className="font-mono">{result.safety_backup.filename}</code>
              {' '}({formatBytes(result.safety_backup.size_bytes)}).
            </p>
          )}
        </div>
      </div>

      <StepsTable steps={result?.steps || []} />

      <div className="mt-4 flex justify-end">
        <button type="button" onClick={onClose} className="rounded-lg bg-blue hover:bg-blue-deep px-3 py-1.5 text-sm font-medium text-white">
          Close
        </button>
      </div>
    </>
  );
}

function RestoreFailedStep({ result, error, dryRun, onRetry, onClose }) {
  const rolled = result?.rolled_back;
  return (
    <>
      <div className="mb-4 flex items-start gap-2 border-l-4 border-l-err bg-err-bg p-3 text-[13px] text-err">
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold">
            {dryRun ? 'Dry-run failed' : 'Restore failed'}
          </p>
          {error && (
            <p className="text-xs mt-0.5 break-all">
              <code className="font-mono">{error.code}</code>
              {error.upstream_status ? <> · upstream {error.upstream_status}</> : null}
              {error.detail ? <>: {error.detail}</> : null}
            </p>
          )}
          {result?.error && (
            <p className="text-xs mt-0.5">
              <code className="font-mono">{result.error.step}</code>: {result.error.detail}
            </p>
          )}
          {rolled && (
            <p className={`text-xs mt-1 ${rolled === 'succeeded' ? 'text-ok' : 'text-err'}`}>
              Rollback: <code className="font-mono">{rolled}</code>.
              {rolled === 'succeeded' && ' Site restored to pre-restore state from safety backup.'}
              {rolled === 'failed' && ' Safety backup still available; manual recovery needed.'}
            </p>
          )}
          {result?.safety_backup && (
            <p className="text-xs mt-1 text-err">
              Safety backup: <code className="font-mono">{result.safety_backup.filename}</code>
            </p>
          )}
        </div>
      </div>

      {result?.steps?.length > 0 && <StepsTable steps={result.steps} />}

      <div className="mt-4 flex justify-between gap-2">
        <button type="button" onClick={onClose} className="rounded-lg border border-bd-strong bg-white hover:bg-s-2 px-3 py-1.5 text-sm text-t2">
          Close
        </button>
        <button type="button" onClick={onRetry} className="rounded-ctl bg-warn px-3.5 py-2 text-[13px] font-medium leading-none text-white transition-colors hover:brightness-110">
          Retry
        </button>
      </div>
    </>
  );
}

function ReachabilityResult({ data, domain }) {
  if (!data.reachable) {
    return (
      <div className="mt-3 flex items-start gap-2 border-l-4 border-l-err bg-err-bg p-3 text-[13px] text-err">
        <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="font-medium">
            Unreachable <span className="font-mono text-xs font-normal text-err">· {data.error}</span>
          </p>
          {data.detail && (
            <p className="text-xs mt-0.5 break-all font-mono text-err">{data.detail}</p>
          )}
          <p className="text-xs mt-1 text-err/80">Checked in {data.elapsed_ms} ms</p>
        </div>
      </div>
    );
  }

  const expected = `https://${domain}/`;
  const redirectedAway = data.final_url && data.final_url !== expected;
  const statusBad = data.status >= 400;
  const tone = statusBad || redirectedAway ? 'amber' : 'emerald';

  const cls = tone === 'amber'
    ? 'border-warn-bd bg-warn-bg text-[#8B5206]'
    : 'border-bd bg-s-green text-ok';

  const Icon = tone === 'amber' ? AlertTriangle : CheckCircle2;

  return (
    <div className={`mt-3 rounded-lg border p-3 text-sm flex items-start gap-2 ${cls}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="font-medium">
          Responded <span className="font-mono">{data.status}</span> in {data.elapsed_ms} ms
        </p>
        {data.final_url && (
          <p className="text-xs mt-0.5 break-all font-mono">
            →{' '}
            <a
              href={data.final_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline inline-flex items-center gap-1"
            >
              {data.final_url}
              <ExternalLink className="w-3 h-3" />
            </a>
            {redirectedAway && <span className="ml-1 italic">(redirected)</span>}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Shared bits ───────────────────────────────────────────────────────────

/**
 * This page's section wrapper. Kept rather than swapped for Card + CardHeader:
 * it is used ~10 times with an 11px eyebrow header, which is a different shape
 * from CardHeader's 16px title + description. It now composes Card so the
 * surface, border and radius come from one place.
 */
function SectionCard({ title, icon: Icon, action, children }) {
  return (
    <Card>
      <div className="flex items-center gap-2 border-b border-bd bg-s-1 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.09em] text-t3">
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
        <span className="flex-1">{title}</span>
        {action}
      </div>
      <div className="space-y-2 p-4">{children}</div>
    </Card>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="w-28 shrink-0 text-[12px] text-t3">{label}</span>
      <span className="min-w-0 flex-1 text-[13.5px] text-t1">{children}</span>
    </div>
  );
}

/** Delegates to Table's Empty so the two "no value" dashes can't drift apart. */
function Muted() {
  return <Empty />;
}

function DateCell({ value }) {
  if (!value) return <Muted />;
  const date = new Date(value.includes('T') ? value : value.replace(' ', 'T') + 'Z');
  if (Number.isNaN(date.getTime())) {
    return <span className="font-mono text-[12px] text-t3">{value}</span>;
  }
  return (
    <span className="text-[12.5px] text-t2" title={date.toISOString()}>
      {date.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })}
    </span>
  );
}

function ordinal(n) {
  if (n >= 11 && n <= 13) return `${n}th`;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 10;
  return `${n}${s[v] || s[0]}`;
}
